import {customElement, property, queryAssignedElements} from 'lit/decorators.js';
import { checkboxGroupStyles } from '@/components/checkbox-group/checkbox-group.styles';
import {SbkLabelMixin} from "@/components/label/label-mixin";
import {html, LitElement} from "lit";
import {FormValidationMixin} from "@/components/mixins/form-validation-mixin";
import {labelStyles} from "@/components/label/label.styles";
import { when } from 'lit/directives/when.js';
import {CheckedValidator, MinimumCheckboxValidator, DataAgreementValidator, Validator} from "@/components/validators/validators";
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import {SbkCheckbox} from "@/components/checkbox/checkbox";
import {unsafeHTML} from "lit/directives/unsafe-html.js";

/**
 * An example element.
 *
 * @slot - This element has a slot
 */
@customElement('sbk-checkbox-group')
export class SbkCheckboxGroup extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {

    @property()
    description: string = ''

    @property()
    name: string = ''

    @property()
    direction: 'horizontal' | 'vertical' = 'vertical'

    @property({type: Boolean})
    disabled: boolean = false

    @queryAssignedElements({selector: 'sbk-checkbox'})
    _checkboxes!: Array<SbkCheckbox>;

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': CheckedValidator,
        'minCheckbox': MinimumCheckboxValidator,
        'validateCheckBoxes': CheckedValidator,
        'validateDataAgreement': DataAgreementValidator
    }

    constructor() {
        super();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
            this.triggerFormControlDependencyChangeEvent();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
            this.triggerFormControlDependencyChangeEvent();
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
    }


    static get styles() {
        return [
            labelStyles,
            checkboxGroupStyles
        ];
    }

    render() {
        return html`
            <fieldset @checkbox-change=${this._checkValidity} class="checkbox-group checkbox-group--${this.direction}${this.invalid ? ' input__wrapper--error' : ''}${this.disabled ? ' input__wrapper--disabled' : ''}" aria-describedby="${this.id}-annotation" aria-labelledby="groupLabel">
                ${this.labelTemplate("groupLabel", true, this.invalid)}
                ${when(this.description,
                    () => html`
                            <sbk-annotation id="${this.id}-annotation" variant="info" ?disabled=${this.disabled}>${unsafeHTML(this.description)}</sbk-annotation>
                        `)}
                <slot @slotchange=${this._onSlotChange} class="checkbox-slot"></slot>
                ${this.invalid ? this.validationErrorTemplate(`${this.id}-annotation`) : ""}
            </fieldset>
        `;
    }

    _checkValidity() {
        const validators = this.getValidators();
        this.handleErrors(validators, '');
    }

    _onSlotChange() {
        this._checkboxes.forEach(checkbox => {
            checkbox.multiple = true;
        });
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this._checkboxes.forEach(checkbox => {
            checkbox.resetValue()
            checkbox.triggerFormControlDependencyChangeEvent();
        });
    }

    setValue(): void {
        this._checkboxes.forEach(checkbox => {
            checkbox.setValue()
            checkbox.triggerFormControlDependencyChangeEvent();
        });
    }

    _required(): void {
        this.required = true;
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.required = false;
        this.removeAttribute('required');
        this.invalid = false;
    }

    triggerFormControlDependencyChangeEvent() {
        const formControlDependencyChangeEvent = new CustomEvent('form-control-dependency-change', {
            bubbles: true
        });
        this.dispatchEvent(formControlDependencyChangeEvent);
    }
}
